// packages/web/src/lib/useNavDestinations.ts — Core hardening 2026-07-29,
// Item 8: resolves which nav destinations to render, once per session.
// Calls GET /api/team/me; if the response carries `navGroups` (the pack
// declared a NavRolesPackConfig) they are flattened into the rail, else a
// flat `navTabs` array is mapped straight across (this fork's own
// TAB_PATHS/TopTab mapping still applies — a pack's `tab` string must match
// one of this fork's own routable TopTab values, which is exactly why the
// server passes through opaque strings rather than trying to validate them
// against a fork-specific union Core can't see).
//
// ⚑ AND WHAT IT ANSWERS WHEN THE SERVER HAS NOT (2026-09-01). It used to
// answer DEFAULT_NAV_DESTINATIONS — the July Actor rail — as BOTH the
// initial state and the permanent catch on a failed request. On CallToWrap
// that meant every load began on another pack's tabs (Career among them,
// lighting gold), and a `/api/team/me` that never succeeded left them there
// for the whole session with nothing said. A pack's rail is not a sensible
// guess about a different pack.
//
// So: unknown is EMPTY (the bottom bar already renders a Knot-only shell —
// the personal pack declares no tabs at all), a failure is empty and NAMED,
// and the July rail is the answer only for the pack that declares it, and
// only once the server has confirmed it declares nothing else.
import { useCallback, useEffect, useState } from 'preact/hooks';
import { ApiError, getTeamMe, type NavGroup, type NavTabDef } from './api';
import { defaultRailFor, splitPhoneRail, type NavDestination } from '../components/navModel';
import type { PackVertical, TopTab } from '../routes/route';

function toDestination(t: NavTabDef): NavDestination {
  return { tab: t.tab as TopTab, label: t.label, short: t.short ?? t.label };
}

/** S1 (wf02 §01b): flattens the server-resolved GROUPS into one rail —
 * the union of everything the membership holds, deduped by tab key
 * (first occurrence wins: the wireframe shows Techs in both the admin and
 * dispatch groups; the union rail shows it once, in the earlier group),
 * each destination tagged with the group it renders under. */
function fromGroups(groups: NavGroup[]): NavDestination[] {
  const seen = new Set<string>();
  const out: NavDestination[] = [];
  for (const g of groups) {
    for (const t of g.tabs) {
      if (seen.has(t.tab)) continue;
      seen.add(t.tab);
      out.push({ tab: t.tab as TopTab, label: t.label, short: t.short ?? t.label, group: g.key, groupLabel: g.label });
    }
  }
  return out;
}

/** THE PHONE'S BAR (Huan, 2026-08-25). The union `fromGroups` builds is
 * what a side rail is for; a bottom bar holding eleven destinations ran off
 * both edges and pushed the centred Knot off centre — hiding authority more
 * completely than any menu would, and without saying so.
 *
 * So when the LANDING group names a phone set, the phone shows that set.
 * Only the landing group's, deliberately: it is the role the server seated
 * this person on, and reading a second group's phone set would be inventing
 * a union nobody declared. Nothing is hidden — Find reaches every
 * destination — and the rail is untouched, which is why this takes `phone`
 * rather than deciding for itself. */
function phoneSetOf(groups: NavGroup[]): NavDestination[] | null {
  const landing = groups[0];
  if (!landing?.phoneTabs?.length) return null;
  return landing.phoneTabs.map((t) => ({
    tab: t.tab as TopTab,
    label: t.label,
    short: t.short ?? t.label,
    group: landing.key,
  }));
}

/** Whether asking the same question again could produce a different answer.
 * A 401/403/404 is a refusal, not a hiccup, and the copy doctrine's own rule
 * is that a permanent refusal gets no retry button. */
function isRetryable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true;
  return err.status >= 500 || err.status === 408 || err.status === 429;
}

/** The server's own sentence, never a sentence of ours. */
function failureOf(err: unknown): string {
  const message = err instanceof Error ? err.message.trim() : '';
  return message || 'the request did not reach the server.';
}

/** Where the rail resolution stands. `resolving` and `failed` both carry an
 * EMPTY rail — the two states in which this app used to show the July pack's
 * tabs to whoever was looking. */
export type NavStatus = 'resolving' | 'ready' | 'failed';

/** What the phone bar shows, and what "More" holds behind it.
 *
 * `overflow` is DERIVED — the rail's full union minus whatever the phone bar
 * already carries — so it can never disagree with the bar, and a tab added to
 * a pack's nav role cannot be stranded off the phone by someone forgetting a
 * second list. On the rail (and on any pack that declares no phone set) the
 * bar IS the union and the overflow is empty, which is why the same hook
 * serves both shells.
 */
export interface NavModel {
  destinations: NavDestination[];
  overflow: NavDestination[];
  status: NavStatus;
  /** Set only on `failed`. */
  failure?: string;
  /** Set only on a `failed` the server might answer differently. */
  retry?: () => void;
}

export function useNavModel(tenantId: string, phone = false, vertical: PackVertical = 'actors'): NavModel {
  const [attempt, setAttempt] = useState(0);
  const [model, setModel] = useState<NavModel>({
    destinations: [],
    overflow: [],
    status: 'resolving',
  });
  const again = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setModel({ destinations: [], overflow: [], status: 'resolving' });
    getTeamMe(tenantId)
      .then(({ navTabs, navGroups }) => {
        if (cancelled) return;
        if (navGroups && navGroups.length > 0) {
          const all = fromGroups(navGroups);
          // MERGE 2026-08-26 — two split rules, composed as declared-wins:
          //   1. A pack that DECLARES phoneTabs (Rexx) gets exactly what it
          //      declared — phoneSetOf, unchanged.
          //   2. A pack that declares nothing but overflows the bar
          //      (actor_assist's nine tabs, the cockpit's seven) gets Lane
          //      A's DERIVED split — landing group first, More holds the
          //      rest — the behaviour Huan blessed on the phone 2026-08-19,
          //      and the same door his 2026-08-25 direction asked of the
          //      cockpit. A rail that fits stays whole (splitPhoneRail
          //      returns everything under the slot count).
          const bar = (phone ? phoneSetOf(navGroups) : null) ?? (phone ? splitPhoneRail(all).shown : all);
          const shown = new Set(bar.map((d) => d.tab));
          setModel({ destinations: bar, overflow: all.filter((d) => !shown.has(d.tab)), status: 'ready' });
        } else if (navTabs) {
          setModel({ destinations: navTabs.map(toDestination), overflow: [], status: 'ready' });
        } else {
          // The server answered, and its answer is that this pack declares
          // no nav config — which is the July Actor pack's own truth and
          // nobody else's. `defaultRailFor` is where that stays honest.
          setModel({ destinations: defaultRailFor(vertical), overflow: [], status: 'ready' });
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setModel({
          destinations: [],
          overflow: [],
          status: 'failed',
          failure: failureOf(err),
          retry: isRetryable(err) ? again : undefined,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, phone, vertical, attempt, again]);

  return model;
}
